import { defineAsyncComponent } from "vue"
import Layout from "layouts/Layout.vue"
import InnerLayout from "layouts/InnerLayout.vue"
import Spinner from "components/Spinner.vue"

function loadView(loader) {
    return defineAsyncComponent({
        loader,
        loadingComponent: Spinner,
        delay: 0,
    })
}

const Home = loadView(() => import("components/Home.vue"))
const Login = loadView(() => import("components/Login.vue"))
const Logout = loadView(() => import("components/Logout.vue"))
const Registration = loadView(() => import("components/Registration.vue"))
const NotFound = loadView(() => import("components/NotFound.vue"))
const Forgot = loadView(() => import("components/Forgot.vue"))
const Reset = loadView(() => import("components/Reset.vue"))
const Confirm = loadView(() => import("components/Confirm.vue"))
const Unsubscribe = loadView(() => import("components/Unsubscribe.vue"))
const Invoice = loadView(() => import("components/Invoice.vue"))
const Review = loadView(() => import("components/Review.vue"))
const Features = loadView(() => import("components/Features.vue"))
const MCP = loadView(() => import("components/MCP.vue"))
const FAQ = loadView(() => import("components/FAQ.vue"))
const Status = loadView(() => import("components/Status.vue"))
const Contact = loadView(() => import("components/Contact.vue"))
const Pricing = loadView(() => import("components/Pricing.vue"))
const Blog = loadView(() => import("components/Blog.vue"))
const BlogPost = loadView(() => import("components/BlogPost.vue"))
const Changelog = loadView(() => import("components/Changelog.vue"))
const TgComplete = loadView(() => import("components/TgComplete.vue"))
const Dashboard = loadView(() => import("components/inner/Dashboard.vue"))
const Tokens = loadView(() => import("components/inner/Tokens.vue"))
const AffPromoCodes = loadView(() => import("components/inner/AffPromoCodes.vue"))
const AffOrderPayout = loadView(() => import("components/inner/AffOrderPayout.vue"))
const Billing = loadView(() => import("components/inner/Billing.vue"))
const Settings = loadView(() => import("components/inner/Settings.vue"))
const Plan = loadView(() => import("components/inner/Plan.vue"))
const ChoosePlan = loadView(() => import("components/inner/ChoosePlan.vue"))
const Pay = loadView(() => import("components/inner/Pay.vue"))

export default [
    {
        path: "/",
        name: "Outer",
        redirect: "/",
        component: Layout,
        children: [
            {
                path: "/login",
                name: "Login",
                component: Login,
                meta: {
                    title: "Login",
                    description: "Sign in to LamaTok to access TikTok data API, manage your tokens, and monitor usage.",
                    noindex: true
                },
            },
            {
                path: "/forgot",
                name: "Forgot",
                component: Forgot,
                meta: {
                    title: "Forgot Password",
                    description: "Reset your LamaTok account password.",
                    noindex: true
                },
            },
            {
                path: "/reset/:uid/:token",
                name: "Reset",
                component: Reset,
                meta: {
                    title: "Reset Password",
                    noindex: true
                },
            },
            {
                path: "/confirm/:uid/:token",
                name: "Confirm",
                component: Confirm,
                meta: {
                    title: "Confirm",
                    noindex: true
                },
            },
            {
                path: "/unsubscribe/:uid/:token/:code",
                name: "Unsubscribe",
                component: Unsubscribe,
                meta: {
                    title: "Unsubscribe",
                    noindex: true
                },
            },
            {
                path: "/logout",
                name: "Logout",
                component: Logout,
                meta: {
                    title: "Logout",
                    noindex: true
                },
            },
            {
                path: "/registration",
                name: "Registration",
                component: Registration,
                meta: {
                    title: "Sign Up - Create Your Account",
                    description: "Create a free LamaTok account. Get 100 free API requests. Access TikTok data instantly.",
                    keywords: "sign up, register, create account, TikTok API, free trial",
                    noindex: true
                },
            },
            {
                path: "/tg-complete",
                name: "TgComplete",
                component: TgComplete,
                meta: {
                    title: "Complete Registration",
                    noindex: true
                },
            },
            {
                path: "/404",
                name: "404",
                component: NotFound,
                meta: {
                    title: "404",
                    noindex: true
                },
            },
            {
                path: "/",
                name: "Home",
                component: Home,
                meta: {
                    title: "LamaTok — Unofficial TikTok Private API for Developers",
                    description: "Unofficial TikTok Private API: profiles, followers, videos, comments, hashtags, search. From $0.60 per 1,000 requests, 100 free on signup, no blocks.",
                    keywords: "LamaTok, TikTok API, TikTok private API, TikTok data, TikTok scraper, TikTok analytics, TikTok automation, social media API"
                },
            },
            {
                path: "/p/:promocode",
                name: "Promo",
                component: Home,
                meta: {
                    title: "TikTok API for Developers",
                    description: "Unofficial TikTok Private API. Fault-tolerant RESTful API for TikTok analytics and SMM automation.",
                    noindex: true
                },
            },
            {
                path: "/promo/:promocode",
                name: "PromoHome",
                component: Home,
                meta: {
                    title: "TikTok API for Developers",
                    description: "Unofficial TikTok Private API. Fault-tolerant RESTful API for TikTok analytics and SMM automation.",
                    noindex: true
                },
            },
            {
                path: "/features",
                name: "Features",
                component: Features,
                meta: {
                    title: "Features - API Capabilities",
                    description: "Explore LamaTok features: user profiles, followers and following, videos, liked videos, playlists, comments, hashtags, search, and video and music downloads for TikTok. Read-only.",
                    keywords: "TikTok API features, user profiles API, videos API, comments API, followers API, hashtag API, TikTok video download API"
                },
            },
            {
                path: "/mcp",
                name: "MCP",
                component: MCP,
                meta: {
                    title: "MCP Server — Use LamaTok from Claude, Cursor & AI Assistants",
                    description: "Use LamaTok directly from Claude, Cursor, OpenAI Codex, Zed, Windsurf, and any MCP-compatible AI assistant. TikTok API tools auto-generated from our OpenAPI spec. Install in one command.",
                    keywords: "TikTok API MCP, LamaTok MCP server, Claude TikTok, Cursor TikTok, MCP server, Model Context Protocol, AI TikTok data"
                },
            },
            {
                path: "/faq",
                name: "FAQ",
                component: FAQ,
                meta: {
                    title: "FAQ - Frequently Asked Questions",
                    description: "Common questions about LamaTok: pricing, rate limits, data freshness, supported endpoints, and integration guides.",
                    keywords: "FAQ, frequently asked questions, TikTok API help, API documentation"
                },
            },
            {
                path: "/status",
                name: "Status",
                component: Status,
                meta: {
                    title: "API Status",
                    description: "Real-time LamaTok API health monitoring. Check endpoint availability and response times.",
                    keywords: "API status, uptime, health check, TikTok API status"
                },
            },
            {
                path: "/contact",
                name: "Contact",
                component: Contact,
                meta: {
                    title: "Contact LamaTok — TikTok API Support",
                    description: "Reach LamaTok support by email or Telegram for billing, API keys and technical questions about the TikTok API, plus service status and security reports.",
                    keywords: "LamaTok contact, TikTok API support, LamaTok email, LamaTok Telegram"
                },
            },
            {
                path: "/pricing",
                name: "Pricing",
                component: Pricing,
                meta: {
                    title: "TikTok API Pricing: $0.60 per 1,000 Requests",
                    description: "TikTok API cost: $0.60 per 1,000 requests at volume, $1 per 1,000 on the popular plan. Pay per request, no subscription, no lock-in. 100 free requests.",
                    keywords: "API pricing, TikTok API pricing, data API cost, subscription plans"
                },
            },
            {
                path: "/reviews",
                name: "Reviews",
                component: Review,
                meta: {
                    title: "Reviews",
                    description: "Read reviews from LamaTok users. See what developers say about our TikTok API.",
                    keywords: "LamaTok reviews, TikTok API reviews, user feedback"
                },
            },
            {
                path: "/blog",
                name: "Blog",
                component: Blog,
                meta: {
                    title: "Blog - Weekly Digest",
                    description: "Weekly industry digests covering TikTok API updates, data scraping news, and platform changes.",
                    keywords: "TikTok API blog, weekly digest, API news"
                },
            },
            {
                path: "/blog/:slug",
                name: "BlogPost",
                component: BlogPost,
                meta: {
                    title: "Blog",
                    description: "Weekly industry digests covering TikTok API updates, data scraping news, and platform changes.",
                    keywords: "TikTok API blog, weekly digest, API news"
                },
            },
            {
                path: "/changelog",
                name: "Changelog",
                component: Changelog,
                meta: {
                    title: "Changelog — Latest API Updates",
                    description: "LamaTok changelog. Latest updates, new endpoints, bug fixes, and improvements.",
                    keywords: "TikTok API changelog, API updates, new features, release notes"
                },
            },
        ]
    },
    {
        path: "/dashboard",
        name: "Inner",
        redirect: "/dashboard",
        component: InnerLayout,
        children: [
            {
                path: "",
                name: "Dashboard",
                component: Dashboard,
                meta: {
                    title: "Dashboard"
                },
            },
            {
                path: "/tokens",
                name: "Tokens",
                component: Tokens,
                meta: {
                    title: "Tokens"
                },
            },
            {
                path: "/affiliate",
                name: "Affiliate program",
                component: AffPromoCodes,
                meta: {
                    title: "Affiliate program"
                },
            },
            {
                path: "/affiliate/order",
                name: "Order payout",
                component: AffOrderPayout,
                meta: {
                    title: "Order payout"
                },
            },
            {
                path: "/billing",
                name: "Billing",
                component: Billing,
                meta: {
                    title: "Billing"
                },
            },
            {
                path: "/billing/pay",
                name: "Pay",
                component: Pay,
                meta: {
                    title: "Pay",
                    noindex: true
                },
            },
            {
                path: "/plan",
                name: "Plan",
                component: Plan,
                meta: {
                    title: "Plan",
                    noindex: true
                },
            },
            {
                path: "/plan/choose/:id",
                name: "Choose plan",
                component: ChoosePlan,
                meta: {
                    title: "Choose plan",
                    noindex: true
                },
            },
            {
                path: "/settings",
                name: "Settings",
                component: Settings,
                meta: {
                    title: "Settings",
                    noindex: true
                },
            },
        ]
    },
    {
        path: "/invoice/:id",
        name: "Invoice",
        component: Invoice,
        meta: {
            title: "Invoice"
        },
    },
    {
        path: "/:pathMatch(.*)*",
        component: NotFound,
        meta: {
            title: "404"
        }
    }
]
